import { Alert, Box, Grid, Skeleton, Stack, Typography } from "@mui/material";
import { getFullName } from "encamp-shared/src/utils/name";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { SubmissionStepType } from "generated-graphql/graphql";
import { useWasteStepper } from "hooks/useWasteStepper";
import { ReactNode } from "react";
import { humanizeStateFieldLabel } from "routes/Customer/Facility/humanizeStateFieldLabel";
import { V2DisplayOnlyField as DisplayOnlyField } from "routes/Customer/Facility/ProfileV2/V2DisplayOnlyField";
import { formatDateLikeForDisplay } from "util/dates";

import { FrozenFacilityProfilePanel } from "./FrozenFacilityProfilePanel";
import { FrozenFilingNotificationsPanel } from "./FrozenFilingNotificationsPanel";
import {
  EMPTY,
  Resolved870012Content,
  ResolvedContact,
  ResolvedStateField,
  SnapshotStoredContent,
  useFrozenSnapshotContent,
} from "./frozenSnapshot";
import { FrozenSubmissionBanner } from "./FrozenSubmissionBanner";

function contactDisplayName(contact: ResolvedContact): string {
  const name = contact.name ?? `${contact.first ?? ""} ${contact.last ?? ""}`.trim();
  return name || "";
}

function formatStateFieldValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.join(", ");
  return String(value);
}

/**
 * §10a generator status + §10b/c federal & state waste codes, read-only from the
 * snapshot. (EPA IDs live on the Facility Profile panel, matching the editable
 * stepper — they are not part of the Waste Activity step.)
 */
function FrozenWasteActivityPanel({ resolved }: { resolved: Resolved870012Content }) {
  const federalWasteCodes = resolved.wasteCodes?.federalWasteCodes ?? [];
  const stateWasteCodes = resolved.wasteCodes?.stateWasteCodes ?? [];
  const calculatedFederalGeneratorStatus =
    resolved.generatorStatus?.calculatedFederalGeneratorStatus ?? null;
  const calculatedStateGeneratorStatus =
    resolved.generatorStatus?.calculatedStateGeneratorStatus ?? null;

  return (
    <Box>
      <Typography variant="h6" mb={3}>
        🗑️ Waste Activity
      </Typography>

      <Typography variant="subtitle2" gutterBottom>
        Generator Status
      </Typography>
      <Grid container columnSpacing={3} rowSpacing={1} mb={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <DisplayOnlyField
            label="Calculated Federal Generator Status"
            text={
              calculatedFederalGeneratorStatus
                ? prettyPrintEnumValue(calculatedFederalGeneratorStatus)
                : ""
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <DisplayOnlyField
            label="Calculated State Generator Status"
            text={
              calculatedStateGeneratorStatus
                ? prettyPrintEnumValue(calculatedStateGeneratorStatus)
                : ""
            }
          />
        </Grid>
      </Grid>

      <Grid container columnSpacing={3} rowSpacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <DisplayOnlyField label="Federal Waste Codes" text={federalWasteCodes.join(", ")} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <DisplayOnlyField label="State Waste Codes" text={stateWasteCodes.join(", ")} />
        </Grid>
      </Grid>
    </Box>
  );
}

/** RCRA contacts grouped into their form buckets, read-only from the snapshot. */
function FrozenContactsPanel({ contacts }: { contacts: Resolved870012Content["contacts"] }) {
  const groups: Array<{ label: string; rows: ResolvedContact[] }> = [
    { label: "Site Contact", rows: contacts?.siteContact ?? [] },
    { label: "Owners", rows: contacts?.owners ?? [] },
    { label: "Operators", rows: contacts?.operators ?? [] },
    { label: "Additional Contacts", rows: contacts?.additionalContacts ?? [] },
  ];

  return (
    <Box>
      <Typography variant="h6" mb={3}>
        📇 Contacts
      </Typography>
      <Stack spacing={3}>
        {groups.map((group) => (
          <Box key={group.label}>
            <Typography variant="subtitle2" gutterBottom>
              {group.label}
            </Typography>
            {group.rows.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {EMPTY}
              </Typography>
            ) : (
              <Stack spacing={2}>
                {group.rows.map((contact, idx) => (
                  <Grid container columnSpacing={3} rowSpacing={1} key={idx}>
                    <Grid size={{ xs: 12, sm: 3 }}>
                      <DisplayOnlyField label="Name" text={contactDisplayName(contact)} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 3 }}>
                      <DisplayOnlyField label="Title" text={contact.title ?? ""} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 3 }}>
                      <DisplayOnlyField label="Email" text={contact.email ?? ""} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 3 }}>
                      <DisplayOnlyField
                        label="Roles"
                        text={(contact.reportingRoles ?? [])
                          .map((role) => prettyPrintEnumValue(role))
                          .join(", ")}
                      />
                    </Grid>
                  </Grid>
                ))}
              </Stack>
            )}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

/**
 * Filed RCRA state fields. The snapshot persists only `key`/`value` (the human
 * label and input type live on field definitions, which are not snapshotted), so
 * we derive the label from the key with the same `humanizeStateFieldLabel` the
 * editable step uses — for the RCRA waste fields shown here the live label IS the
 * raw key, so key-only humanizing yields the same readable text as the edit form.
 */
function FrozenStateFieldsPanel({ stateFields }: { stateFields: ResolvedStateField[] }) {
  return (
    <Box>
      <Typography variant="h6" mb={3}>
        🏛️ State Information
      </Typography>
      {stateFields.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No state information was filed.
        </Typography>
      ) : (
        <Grid container columnSpacing={3} rowSpacing={2}>
          {stateFields.map((field, idx) => (
            <Grid size={{ xs: 12, md: 6 }} key={field.key ?? idx}>
              <DisplayOnlyField
                label={
                  <span style={{ whiteSpace: "normal" }}>
                    {field.key ? humanizeStateFieldLabel(field.key, field.key) : ""}
                  </span>
                }
                text={formatStateFieldValue(field.value)}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}

/**
 * Read-only list of the submission's reviewers — who must sign off, their role
 * (reviewer vs signer), approval status, and due date. Read from the live
 * submission (reviewers aren't part of the filed snapshot); shown on the Review
 * step of a frozen submission, mirroring what the live `ReviewStep` datagrid
 * shows but without any edit affordances.
 */
function FrozenReviewersPanel() {
  const { submission } = useWasteStepper();
  const reviewers = [...(submission?.reviewers ?? [])].sort(
    (a, b) => a.reviewOrder - b.reviewOrder
  );

  return (
    <Box>
      <Typography variant="h6" mb={3}>
        ✅ Reviewers
      </Typography>
      {reviewers.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No reviewers were required for this submission.
        </Typography>
      ) : (
        <Stack spacing={2}>
          {reviewers.map((reviewer) => {
            const name = reviewer.signerNameOverride
              ? reviewer.signerNameOverride
              : reviewer.user.person
                ? getFullName(reviewer.user.person)
                : reviewer.user.email;
            return (
              <Grid container columnSpacing={3} rowSpacing={1} key={reviewer.id}>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <DisplayOnlyField label="Name" text={name} />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <DisplayOnlyField label="Role" text={reviewer.isSigner ? "Signer" : "Reviewer"} />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <DisplayOnlyField
                    label="Status"
                    text={
                      reviewer.reviewerStatus
                        ? prettyPrintEnumValue(reviewer.reviewerStatus)
                        : "Pending"
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <DisplayOnlyField
                    label="Due Date"
                    text={reviewer.dueDate ? formatDateLikeForDisplay(reviewer.dueDate) : ""}
                  />
                </Grid>
              </Grid>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}

function renderStepBody(
  step: SubmissionStepType,
  resolved: Resolved870012Content,
  stateFields: ResolvedStateField[]
) {
  switch (step) {
    case SubmissionStepType.FacilityProfile:
      return <FrozenFacilityProfilePanel resolved={resolved} />;
    case SubmissionStepType.RcraWasteActivity:
      return <FrozenWasteActivityPanel resolved={resolved} />;
    case SubmissionStepType.Contacts:
      return <FrozenContactsPanel contacts={resolved.contacts} />;
    case SubmissionStepType.FilingNotifications:
      return <FrozenFilingNotificationsPanel resolved={resolved} />;
    case SubmissionStepType.StateFields:
      return <FrozenStateFieldsPanel stateFields={stateFields} />;
    case SubmissionStepType.Review:
      return <FrozenReviewersPanel />;
    default:
      return (
        <Typography variant="body2" color="text.secondary">
          This section has no filed details to display.
        </Typography>
      );
  }
}

/**
 * Shared loading/error/empty gate for snapshot-backed frozen views. Calls
 * `renderBody` with the resolved content + raw state fields once the snapshot has
 * loaded; otherwise renders the appropriate skeleton / error / no-snapshot state.
 */
function renderFrozenGate(
  state: {
    loading: boolean;
    error: unknown;
    content: SnapshotStoredContent;
    resolved: Resolved870012Content | null;
  },
  renderBody: (resolved: Resolved870012Content, stateFields: ResolvedStateField[]) => ReactNode
): ReactNode {
  if (state.error) {
    return <Alert severity="error">Failed to load the filed submission snapshot.</Alert>;
  }
  if (state.loading && !state.content) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="rounded" height={160} />
        <Skeleton variant="rounded" height={160} />
      </Stack>
    );
  }
  if (!state.resolved) {
    return <Alert severity="warning">No filed snapshot is available for this submission.</Alert>;
  }
  return renderBody(state.resolved, state.content?.stateFields ?? []);
}

/**
 * Read-only body of a single stepper step for a frozen (filed) submission,
 * sourced from the `RCRA_8700_12` snapshot rather than live tables — so it shows
 * exactly what was filed even if the source rows have since drifted. No lifecycle
 * banner (the caller renders that). Reused by `FrozenStepPanel` (the per-step
 * route) and the read-only Overview accordions.
 */
export function FrozenStepContent({
  step,
  submissionId,
}: {
  step: SubmissionStepType;
  submissionId: string;
}) {
  const { content, resolved, loading, error } = useFrozenSnapshotContent(submissionId);

  return renderFrozenGate({ content, resolved, loading, error }, (resolvedContent, stateFields) =>
    renderStepBody(step, resolvedContent, stateFields)
  );
}

/**
 * Read-only render of a single stepper step for a frozen (filed) submission.
 * Mounted by `StepRoute` when `isFrozen`; the read-only lifecycle banner renders
 * above the snapshot body.
 */
export function FrozenStepPanel({
  step,
  submissionId,
}: {
  step: SubmissionStepType;
  submissionId: string;
}) {
  return (
    <Stack spacing={2}>
      <FrozenSubmissionBanner />
      <FrozenStepContent step={step} submissionId={submissionId} />
    </Stack>
  );
}
